import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  PlugDisconnected24Regular,
} from '@fluentui/react-icons';

/**
 * Full task-pane landing shown BEFORE the app is usable, while the auth
 * (whoami) query resolves. Two states:
 *  - loading → calm branded "connexion en cours" with a spinner, no button.
 *  - error   → friendly message + primary "Réessayer" button (onRetry),
 *              optionally surfacing a short technical errorMessage.
 *
 * Themed via the surrounding FluentProvider (follows the Outlook host theme,
 * light or dark) — colors come from Fluent tokens. Robust from ~300px wide up
 * and on short heights: it scrolls rather than clips and never scrolls
 * sideways. The hero is identical across both states, so there is no layout
 * jump when auth flips from loading to error.
 */
export interface LandingViewProps {
  state: 'loading' | 'error';
  onRetry?: () => void;
  errorMessage?: string;
}

const useStyles = makeStyles({
  root: {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // `safe center` keeps the block centred when there is room, but falls back
    // to top-aligned when the pane is shorter than the content, so nothing is
    // clipped above the scroll origin. `center` is the graceful fallback on any
    // host that ignores the keyword.
    justifyContent: 'safe center',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    gap: tokens.spacingVerticalXL,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
  },

  // --- Branded hero ---------------------------------------------------------
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    flexShrink: 0,
  },
  mark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    marginBottom: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  tile: {
    width: '64px',
    height: '64px',
    // A soft neutral drop shadow grounds the tile without the brand-blue glow
    // (which read as generic "AI" gloss). Single-shadow syntax — the CSS
    // drop-shadow() filter takes one shadow and no spread, and it hugs the
    // tile's rounded silhouette. Neutral black reads correctly in both themes.
    filter: 'drop-shadow(0 6px 14px rgba(0, 0, 0, 0.28))',
  },
  kicker: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForeground1,
  },
  wordmark: {
    margin: 0,
    fontSize: '26px',
    lineHeight: '30px',
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
  },
  rule: {
    width: '34px',
    height: '3px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundImage: 'linear-gradient(90deg, #0F6CBD 0%, #2E8CE0 100%)',
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
  tagline: {
    maxWidth: '272px',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground2,
  },

  // --- Shared state block ---------------------------------------------------
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    width: '100%',
    maxWidth: '300px',
    flexShrink: 0,
  },
  stateTitle: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  stateBody: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground2,
    maxWidth: '272px',
  },

  // --- Error specifics (grafted from candidate A) ---------------------------
  errorBadge: {
    display: 'grid',
    placeItems: 'center',
    width: '44px',
    height: '44px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    flexShrink: 0,
  },
  detail: {
    boxSizing: 'border-box',
    alignSelf: 'stretch',
    marginTop: tokens.spacingVerticalXXS,
    // Cap the technical detail so a long, unbroken token scrolls inside its own
    // box instead of pushing the Réessayer button out of the pane.
    maxHeight: '84px',
    overflowY: 'auto',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    // Red-tinted rule (grafted from candidate C) ties the detail to the danger
    // palette; longhand properties keep Griffel happy.
    borderLeftWidth: tokens.strokeWidthThick,
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorPaletteRedBorder2,
    textAlign: 'left',
  },
  detailText: {
    display: 'block',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-word',
  },
  retry: {
    width: '100%',
    marginTop: tokens.spacingVerticalXS,
  },

  // --- Footer ---------------------------------------------------------------
  footer: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

/**
 * Rounded-tile mark echoing the app icon, with a white folder + tucked-in
 * envelope glyph — "an e-mail filed into a project folder". Decorative; the
 * accessible name lives on the wordmark, so this is marked presentational.
 */
function BrandMark() {
  const styles = useStyles();
  return (
    <svg className={styles.tile} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="rmm-tile" x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3B90E0" />
          <stop offset="0.55" stopColor="#0F6CBD" />
          <stop offset="1" stopColor="#0C4C89" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="64" height="64" rx="16" fill="url(#rmm-tile)" />

      {/* folder back panel (tabbed) — filled with the tile gradient so it sits
          seamlessly on the tile, outlined in white */}
      <path
        d="M13 43 L13 24 Q13 22 15 22 L23 22 L26 25.5 L49 25.5 Q51 25.5 51 27.5 L51 43 Z"
        fill="url(#rmm-tile)"
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />

      {/* envelope — rises out of the folder opening */}
      <rect x="20" y="15" width="24" height="20" rx="2.5" fill="#FFFFFF" />
      <path
        d="M21.5 17.6 L32 26 L42.5 17.6"
        fill="none"
        stroke="#0F6CBD"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* folder front pocket — gradient fill covers the envelope's lower edge so
          the mail reads as tucked inside; white outline draws the front lip */}
      <path
        d="M11 44 L15.5 31 L48.5 31 L53 44 Z"
        fill="url(#rmm-tile)"
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function LandingView(props: LandingViewProps) {
  const { state, onRetry, errorMessage } = props;
  const styles = useStyles();
  const isLoading = state === 'loading';
  const detail = errorMessage?.trim();

  return (
    <div className={styles.root}>
      <header className={styles.brand}>
        <div className={styles.mark}>
          <BrandMark />
        </div>
        <span className={styles.kicker}>Chabanne</span>
        <h1 className={styles.wordmark}>Ranger mes mails</h1>
        <div className={styles.rule} aria-hidden />
        <Text className={styles.tagline}>
          Classez vos e-mails dans les dossiers de vos projets et services, sans quitter Outlook.
        </Text>
      </header>

      {isLoading ? (
        <div className={styles.state} role="status" aria-live="polite" aria-busy="true">
          <Spinner size="medium" />
          <Text className={styles.stateTitle}>Connexion en cours…</Text>
          <Text className={styles.stateBody}>Nous vous connectons à votre espace sécurisé.</Text>
        </div>
      ) : (
        <div className={styles.state} role="alert">
          <span className={styles.errorBadge} aria-hidden>
            <PlugDisconnected24Regular />
          </span>
          <Text className={styles.stateTitle}>Connexion impossible</Text>
          <Text className={styles.stateBody}>
            Nous n'avons pas pu vous authentifier. Vérifiez votre connexion réseau, puis
            réessayez. Si le problème persiste, fermez puis rouvrez le volet.
          </Text>
          {detail ? (
            <div className={styles.detail}>
              <Text className={styles.detailText}>{detail}</Text>
            </div>
          ) : null}
          <Button
            className={styles.retry}
            appearance="primary"
            icon={<ArrowClockwiseRegular />}
            onClick={() => onRetry?.()}
            disabled={!onRetry}
          >
            Réessayer
          </Button>
        </div>
      )}

      <Text className={styles.footer} as="span">
        Authentification sécurisée Microsoft 365
      </Text>
    </div>
  );
}
